import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, PartyPopper } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

export default function PaymentResult({ variant }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [reservation, setReservation] = useState(null);

  useEffect(() => {
    if (variant === "cancel" || !sessionId) { setStatus(variant === "cancel" ? "cancelled" : "error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (data.reservation_id) { try { const r = await api.get(`/reservations/${data.reservation_id}`); setReservation(r.data); } catch {} }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("failed"); return; }
      } catch {}
      attempts += 1;
      if (attempts > 12) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, variant]);

  return (
    <div className="min-h-screen flex items-center justify-center pt-28 pb-16 px-6 surface-alt">
      <Seo title="Reservation | Keys Event Center" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card p-10 max-w-lg w-full text-center" data-testid="payment-result">
        {status === "checking" && (<>
          <Loader2 className="mx-auto text-[#ff3b3b] animate-spin mb-5" size={56} />
          <h1 className="font-display text-2xl font-bold text-[#f5f0ff]">Confirming your payment…</h1>
          <p className="text-[#9aa0c0] mt-2">Hang tight, this only takes a moment.</p>
        </>)}
        {status === "paid" && (<>
          <CheckCircle2 className="mx-auto text-[#21e6cf] mb-5" size={64} />
          <h1 className="font-display text-3xl font-extrabold text-[#f5f0ff]">Party Reserved!</h1>
          <p className="text-[#9aa0c0] mt-3">Your reservation is confirmed and a confirmation email is on its way. We can't wait to celebrate with you!</p>
          {reservation && (
            <div className="surface-alt rounded-2xl p-5 mt-6 text-left text-sm space-y-1.5 border border-[#2a2a44]" data-testid="reservation-summary">
              <div className="flex justify-between"><span className="text-[#7a7fa0]">Date</span><span className="font-semibold text-[#f5f0ff]">{reservation.reservation_date}</span></div>
              <div className="flex justify-between"><span className="text-[#7a7fa0]">Start Time</span><span className="font-semibold text-[#f5f0ff]">{reservation.start_time}</span></div>
              <div className="flex justify-between"><span className="text-[#7a7fa0]">Duration</span><span className="font-semibold text-[#f5f0ff]">{reservation.hours} hours</span></div>
              <div className="flex justify-between"><span className="text-[#7a7fa0]">Total Paid</span><span className="font-semibold text-[#21e6cf]">${reservation.amount}</span></div>
              <div className="flex justify-between"><span className="text-[#7a7fa0]">Confirmation</span><span className="font-mono-arcade text-xs text-[#f5f0ff]">{reservation.id?.slice(-8).toUpperCase()}</span></div>
            </div>
          )}
          <Link to="/" className="btn-primary inline-flex items-center gap-2 px-8 py-3.5 mt-7"><PartyPopper size={18} /> Back to Home</Link>
        </>)}
        {["cancelled", "failed", "error", "timeout"].includes(status) && (<>
          <XCircle className="mx-auto text-[#ff3b3b] mb-5" size={64} />
          <h1 className="font-display text-2xl font-bold text-[#f5f0ff]">{status === "cancelled" ? "Payment Cancelled" : status === "timeout" ? "Still Processing" : "Payment Not Completed"}</h1>
          <p className="text-[#9aa0c0] mt-2">{status === "timeout" ? "Your payment is taking longer than expected. Check your email or contact us if you were charged." : "No worries, your reservation wasn't charged. You can try booking again anytime."}</p>
          <Link to="/reservations" className="btn-outline inline-flex px-8 py-3.5 mt-7">Try Again</Link>
        </>)}
      </motion.div>
    </div>
  );
}
